import { X, Trophy } from 'lucide-react';

import { cn } from '@/lib/utils';

interface PrizeTiersDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function PrizeTiersDrawer({ open, onClose }: PrizeTiersDrawerProps) {
  // Chest rewards table with accurate probabilities from documentation
  const rewardTiers = [
    {
      reward: '1 SOL',
      probability: '0.0000384%',
      value: '$200',
      color: '#FEBB01',
      emoji: '🏆'
    },
    {
      reward: '0.025 SOL',
      probability: '0.00808%',
      value: '$5',
      color: '#9333EA',
      emoji: '💎'
    },
    {
      reward: '0.005 SOL',
      probability: '0.0832%',
      value: '$1',
      color: '#3B82F6',
      emoji: '💍'
    },
    {
      reward: '0.001 SOL',
      probability: '0.8586%',
      value: '$0.2',
      color: '#10B981',
      emoji: '💰'
    },
    {
      reward: '10000 Points',
      probability: '2.35%',
      value: '$0.1',
      color: '#F59E0B',
      emoji: '🌟'
    },
    {
      reward: '5000 Points',
      probability: '6.41%',
      value: '$0.05',
      color: '#6B7280',
      emoji: '⭐'
    },
    {
      reward: '2000 Points',
      probability: '24.20%',
      value: '$0.02',
      color: '#6B7280',
      emoji: '✨'
    },
    {
      reward: '1000 Points',
      probability: '66.09%',
      value: '$0.01',
      color: '#6B7280',
      emoji: '💫'
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-96">
      {/* Backdrop (mobile only) */}
      <div className="lg:hidden absolute inset-0 bg-black/95 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 lg:inset-y-0 lg:right-0',
          'border-t lg:border-t-0 lg:border-l',
          'lg:w-96 rounded-t-lg lg:rounded-t-none shadow-xl',
          'max-h-[85vh] lg:max-h-full overflow-hidden',
          'transition-transform duration-300 ease-out',
          'translate-y-0',
          'flex flex-col'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#50E5F8',
          boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.3)' }}>
          <div className="flex items-center space-x-2">
            <Trophy className="h-5 w-5" style={{ color: '#50E5F8' }} />
            <h2 className="font-semibold text-lg" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>CHEST OPENING RULES</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            aria-label="Close rules"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Key Stats Banner */}
        <div className="px-4 py-3" style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)', borderBottom: '1px solid rgba(80, 228, 249, 0.2)' }}>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Cost per Chest
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8' }}>
                0.001 credits
              </div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.5)' }}>
                ≈$0.2
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Expected Value per Draw
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#FEBB01' }}>
                $0.02
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 p-4">
          <div className="space-y-4">
            {/* Rules Section */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)', border: '2px solid rgba(80, 228, 249, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>📋</span>
                <span>RULES</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6' }}>
                <p><strong style={{ color: '#50E5F8' }}>1.</strong> Each chest opening requires a cost of <strong style={{ color: '#FEBB01' }}>0.001 credits</strong>.</p>
                <p><strong style={{ color: '#50E5F8' }}>2.</strong> You can deposit more than 0.001 credits at once to open multiple chests. We will calculate the number of chests based on your total credits amount, with each chest costing 0.001 credits.</p>
                <p><strong style={{ color: '#50E5F8' }}>3.</strong> Every chest guarantees a reward! Possible rewards include:</p>
                <ul className="ml-6 space-y-1 list-disc" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  <li><strong>1000 points</strong></li>
                  <li><strong>2000 points</strong></li>
                  <li><strong>5000 points</strong></li>
                  <li><strong>10000 points</strong></li>
                  <li><strong>0.001 SOL</strong></li>
                  <li><strong>0.005 SOL</strong></li>
                  <li><strong>0.025 SOL</strong></li>
                  <li><strong>1 SOL</strong></li>
                </ul>
              </div>
            </div>

            {/* Rewards Table */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>🎁</span>
                <span>REWARD PROBABILITIES</span>
              </h3>

              <div className="space-y-2">
                {rewardTiers.map((tier, index) => (
                  <div
                    key={index}
                    className="p-3 rounded-lg border-l-4 transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderLeftColor: tier.color,
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{tier.emoji}</span>
                        <div>
                          <div className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>
                            {tier.reward}
                          </div>
                          <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {tier.value} value
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: tier.color }}>
                          {tier.probability}
                        </div>
                        <div className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.5)' }}>
                          chance
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue Distribution */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>💰</span>
                <span>REVENUE DISTRIBUTION</span>
              </h4>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p className="mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>When you open a chest with 0.001 credits, funds are split:</p>

                <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  <div className="flex justify-between">
                    <span>Transaction Fee (Team):</span>
                    <span className="font-bold" style={{ color: '#FB923C' }}>40%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Chest Prize Pool:</span>
                    <span className="font-bold" style={{ color: '#22C55E' }}>10%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Points Lottery Pool:</span>
                    <span className="font-bold" style={{ color: '#9D4EDD' }}>50%</span>
                  </div>
                </div>

                <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'rgba(254, 187, 1, 0.1)', border: '1px solid rgba(254, 187, 1, 0.3)' }}>
                  <p className="text-xs font-semibold" style={{ color: '#FEBB01', fontFamily: 'Outfit, sans-serif' }}>Example per chest:</p>
                  <p className="text-xs mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>• Chest rewards: 0.0001 SOL (≈$0.02)</p>
                  <p className="text-xs" style={{ fontFamily: 'Outfit, sans-serif' }}>• Lottery pool: 0.0005 SOL (≈$0.10)</p>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>💡</span>
                <span>HOW IT WORKS</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Select number of chests via slider or manual input</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Minimum: 1 chest (0.001 credits)</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Maximum: Based on your available balance</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Different animations for different rarity levels</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Multiple chest openings show highest rarity won</p>
              </div>
            </div>

            {/* Reward Categories */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(157, 78, 221, 0.1)', border: '1px solid rgba(157, 78, 221, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                <span>🎯</span>
                <span>REWARD CATEGORIES</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p><strong style={{ color: '#50E5F8' }}>Chest Rewards:</strong> SOL or Points from the chest itself</p>
                <p><strong style={{ color: '#FB8B00' }}>Referral Returns:</strong> Cashback if you've bound a referrer</p>
                <p><strong style={{ color: '#22C55E' }}>Points Rewards:</strong> Points earned from opening activity</p>
              </div>
            </div>

            {/* Important Notes */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <h4 className="font-semibold mb-3 flex items-center space-x-2" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#EF4444' }}>
                <span>⚠️</span>
                <span>IMPORTANT NOTES</span>
              </h4>
              <div className="text-sm space-y-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• All values based on SOL at $200</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• 100% win rate - every chest guarantees a reward!</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• Expected value: $0.02 per draw</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• 50% of each chest funds the Points Lottery</p>
                <p style={{ fontFamily: 'Outfit, sans-serif' }}>• More chest activity = larger lottery prizes!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
